import {DOCUMENT} from '@angular/common';
import {Component, ElementRef, HostListener, Input, OnDestroy, Renderer2, ViewChild, inject} from '@angular/core';
import {DomSanitizer, SafeHtml} from '@angular/platform-browser';
import {buildRichDescriptionTooltipHtml} from '../../text/list-item-description';
import {HOVER_PREVIEW_OPEN_DELAY_MS} from '../../hover-preview.constants';

type TooltipState = {
  left: number;
  top: number;
  width: number;
  maxHeight: number;
};

type TooltipAnchor = {
  x: number;
  y: number;
  rect: DOMRect;
};

const TOOLTIP_EDGE_MARGIN = 8;
const TOOLTIP_GAP = 12;
const TOOLTIP_MAX_WIDTH = 620;
const TOOLTIP_MIN_WIDTH = 280;
const TOOLTIP_CLOSE_DELAY = 80;

@Component({
  selector: 'sm-rich-tooltip',
  templateUrl: './sm-rich-tooltip.component.html',
  styleUrls: ['./sm-rich-tooltip.component.scss'],
  standalone: false,
})
export class SmRichTooltipComponent implements OnDestroy {
  private readonly document = inject(DOCUMENT);
  private readonly elementRef = inject(ElementRef<HTMLElement>);
  private readonly sanitizer = inject(DomSanitizer);
  private readonly renderer = inject(Renderer2);
  private openTimer: number | null = null;
  private positionTimer: number | null = null;
  private closeTimer: number | null = null;
  private panelAttachedToBody = false;
  private anchor: TooltipAnchor | null = null;

  @Input() previewText = '';
  @Input() searchQuery = '';
  @Input() html: string | null | undefined = null;
  @Input() emptyLabel = '';

  @ViewChild('triggerText', {read: ElementRef}) private readonly triggerTextRef?: ElementRef<HTMLElement>;
  @ViewChild('panel', {read: ElementRef}) private readonly panelRef?: ElementRef<HTMLDivElement>;

  tooltipHtml: SafeHtml | null = null;
  tooltipState: TooltipState | null = null;
  tooltipReady = false;

  get displayText(): string {
    return this.previewText.trim() || this.emptyLabel;
  }

  get hasHtmlContent(): boolean {
    return Boolean(this.html?.trim());
  }

  handleTriggerMouseEnter(event: MouseEvent): void {
    if (!this.hasHtmlContent) {
      this.closeTooltip();
      return;
    }

    const triggerElement = this.triggerTextRef?.nativeElement;
    if (!triggerElement) {
      this.closeTooltip();
      return;
    }

    const isClipped = triggerElement.scrollWidth > triggerElement.clientWidth + 1
      || triggerElement.scrollHeight > triggerElement.clientHeight + 1
      || Boolean(this.searchQuery.trim());
    if (!isClipped) {
      this.closeTooltip();
      return;
    }

    this.scheduleOpenTooltip(triggerElement, event);
  }

  handleTriggerMouseLeave(): void {
    this.cancelOpenTooltip();
    this.scheduleCloseTooltip();
  }

  handleTriggerPointerDown(): void {
    this.closeTooltip();
  }

  handleTooltipMouseEnter(): void {
    this.cancelCloseTooltip();
  }

  handleTooltipMouseLeave(): void {
    this.scheduleCloseTooltip();
  }

  closeTooltip(): void {
    this.cancelOpenTooltip();
    this.cancelPositionTimer();
    this.cancelCloseTooltip();
    this.detachPanelFromBody();
    this.tooltipState = null;
    this.tooltipHtml = null;
    this.tooltipReady = false;
    this.anchor = null;
  }

  @HostListener('window:scroll')
  handleWindowScroll(): void {
    this.closeTooltip();
  }

  @HostListener('window:resize')
  handleWindowResize(): void {
    this.closeTooltip();
  }

  @HostListener('document:keydown.escape')
  handleEscape(): void {
    this.closeTooltip();
  }

  @HostListener('document:pointerdown', ['$event'])
  handleDocumentPointerDown(event: Event): void {
    if (!this.isEventInsideTooltip(event.target)) {
      this.closeTooltip();
    }
  }

  @HostListener('document:click', ['$event'])
  handleDocumentClick(event: Event): void {
    if (!this.isEventInsideTooltip(event.target)) {
      this.closeTooltip();
    }
  }

  ngOnDestroy(): void {
    this.closeTooltip();
  }

  private scheduleOpenTooltip(triggerElement: HTMLElement, event?: MouseEvent): void {
    this.cancelOpenTooltip();
    this.openTimer = window.setTimeout(() => {
      this.openTimer = null;
      this.openTooltip(triggerElement, event);
    }, HOVER_PREVIEW_OPEN_DELAY_MS);
  }

  private openTooltip(triggerElement: HTMLElement, event?: MouseEvent): void {
    this.cancelCloseTooltip();
    const tooltipHtml = buildRichDescriptionTooltipHtml(this.html);
    if (!tooltipHtml) {
      this.closeTooltip();
      return;
    }

    this.tooltipHtml = this.sanitizer.bypassSecurityTrustHtml(tooltipHtml);

    const triggerRect = triggerElement.getBoundingClientRect();
    const width = Math.min(TOOLTIP_MAX_WIDTH, Math.max(TOOLTIP_MIN_WIDTH, window.innerWidth - TOOLTIP_EDGE_MARGIN * 2));
    this.anchor = {
      x: event?.clientX ?? triggerRect.left,
      y: event?.clientY ?? triggerRect.bottom,
      rect: triggerRect,
    };

    const left = this.clamp(
      this.anchor.x + TOOLTIP_GAP,
      TOOLTIP_EDGE_MARGIN,
      window.innerWidth - width - TOOLTIP_EDGE_MARGIN,
    );

    this.tooltipState = {
      left,
      top: Math.max(TOOLTIP_EDGE_MARGIN, this.anchor.y + TOOLTIP_GAP),
      width,
      maxHeight: Math.max(120, window.innerHeight - TOOLTIP_EDGE_MARGIN * 2),
    };
    this.tooltipReady = false;

    this.schedulePosition();
  }

  private schedulePosition(): void {
    this.cancelPositionTimer();
    this.positionTimer = window.setTimeout(() => {
      this.positionTimer = null;
      this.positionTooltip();
    }, 0);
  }

  private positionTooltip(): void {
    const panel = this.panelRef?.nativeElement;
    const triggerElement = this.triggerTextRef?.nativeElement;

    if (!panel || !triggerElement || !this.tooltipState) {
      return;
    }

    this.attachPanelToBody(panel);

    const triggerRect = this.anchor?.rect ?? triggerElement.getBoundingClientRect();
    const panelRect = panel.getBoundingClientRect();
    const viewportMargin = TOOLTIP_EDGE_MARGIN;
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const anchorX = this.anchor?.x ?? triggerRect.right;
    const anchorY = this.anchor?.y ?? triggerRect.bottom;

    let left = this.tooltipState.left;
    let top = anchorY + TOOLTIP_GAP;

    if (left + panelRect.width > viewportWidth - viewportMargin) {
      left = viewportWidth - panelRect.width - viewportMargin;
    }

    if (left < viewportMargin) {
      left = viewportMargin;
    }

    if (top + panelRect.height > viewportHeight - viewportMargin) {
      const aboveTop = anchorY - TOOLTIP_GAP - panelRect.height;
      top = aboveTop >= viewportMargin ? aboveTop : viewportMargin;
    }

    if (top < viewportMargin) {
      top = viewportMargin;
    }

    const nearCursorLeft = anchorX + TOOLTIP_GAP;
    const nearCursorRight = anchorX - TOOLTIP_GAP - panelRect.width;
    const canOpenNearCursorRight = nearCursorRight >= viewportMargin;
    const canOpenNearCursorLeft = nearCursorLeft + panelRect.width <= viewportWidth - viewportMargin;

    if (canOpenNearCursorLeft) {
      left = nearCursorLeft;
    } else if (canOpenNearCursorRight) {
      left = nearCursorRight;
    }

    if (left + panelRect.width > viewportWidth - viewportMargin) {
      left = viewportWidth - panelRect.width - viewportMargin;
    }

    this.tooltipState = {
      left,
      top,
      width: this.tooltipState.width,
      maxHeight: Math.max(120, viewportHeight - viewportMargin * 2),
    };
    this.tooltipReady = true;
  }

  private attachPanelToBody(panel: HTMLElement): void {
    if (this.panelAttachedToBody || panel.parentElement === this.document.body) {
      this.panelAttachedToBody = true;
      return;
    }

    this.renderer.appendChild(this.document.body, panel);
    this.panelAttachedToBody = true;
  }

  private detachPanelFromBody(): void {
    const panel = this.panelRef?.nativeElement;
    if (!this.panelAttachedToBody || !panel || panel.parentElement !== this.document.body) {
      this.panelAttachedToBody = false;
      return;
    }

    this.renderer.removeChild(this.document.body, panel);
    this.panelAttachedToBody = false;
  }

  private scheduleCloseTooltip(): void {
    this.cancelCloseTooltip();
    this.closeTimer = window.setTimeout(() => {
      this.closeTimer = null;
      this.closeTooltip();
    }, TOOLTIP_CLOSE_DELAY);
  }

  private cancelCloseTooltip(): void {
    if (this.closeTimer === null) {
      return;
    }

    window.clearTimeout(this.closeTimer);
    this.closeTimer = null;
  }

  private cancelOpenTooltip(): void {
    if (this.openTimer === null) {
      return;
    }

    window.clearTimeout(this.openTimer);
    this.openTimer = null;
  }

  private cancelPositionTimer(): void {
    if (this.positionTimer === null) {
      return;
    }

    window.clearTimeout(this.positionTimer);
    this.positionTimer = null;
  }

  private isEventInsideTooltip(target: EventTarget | null): boolean {
    if (!(target instanceof Node)) {
      return false;
    }

    return this.panelRef?.nativeElement.contains(target) ?? false;
  }

  private clamp(value: number, min: number, max: number): number {
    return Math.min(Math.max(value, min), max);
  }
}
